import React, { useId } from 'react';

import { CalendarIcon, ChevronDownIcon } from '../../../assets/icons/icons';
import { DateRangeCalendar, type DateRangeCalendarProps } from './DateRangeCalendar';
import { Popover } from './Popover';
import { usePopover } from './usePopover';
import { formatDateRangeLabel, type DateRange } from './dateUtils';
import styles from './DatePicker.module.scss';

export interface DateRangePickerProps extends Omit<DateRangeCalendarProps, 'months'> {
    disabled?: boolean;
    compact?: boolean;
}

export const DateRangePicker = ({ value, onChange, disabled = false, compact = false, ...calendar }: DateRangePickerProps) => {
    const { isOpen, setIsOpen, rootRef, popoverRef, position } = usePopover<HTMLDivElement>();
    const popoverId = useId();

    const toggle = () => setIsOpen((open) => !open);

    const handleChange = (range: DateRange | null) => {
        onChange(range);
        if (range) setIsOpen(false);
    };

    return (
        <div className={styles.picker} ref={rootRef}>
            {compact ? (
                <button
                    type="button"
                    className={`${styles.iconTrigger} ${value ? styles.iconTriggerActive : ''}`}
                    onClick={toggle}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? popoverId : undefined}
                    aria-label={value ? `Период: ${formatDateRangeLabel(value)}` : 'Выбрать период'}
                    title={value ? formatDateRangeLabel(value) : 'Выбрать период'}
                >
                    <CalendarIcon className={styles.icon} />
                </button>
            ) : (
                <button
                    type="button"
                    className={styles.trigger}
                    onClick={toggle}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-controls={isOpen ? popoverId : undefined}
                >
                    <span className={styles.triggerLeading}>
                        <CalendarIcon className={styles.icon} />
                    </span>
                    <span className={value ? styles.triggerValue : styles.triggerPlaceholder}>
                        {formatDateRangeLabel(value)}
                    </span>
                    <span className={styles.triggerTrailing}>
                        <ChevronDownIcon className={styles.icon} />
                    </span>
                </button>
            )}

            {isOpen && (
                <Popover id={popoverId} label="Выбор периода" popoverRef={popoverRef} position={position}>
                    <DateRangeCalendar key={String(isOpen)} value={value} onChange={handleChange} {...calendar} />
                </Popover>
            )}
        </div>
    );
};
